import {
  Button,
  Col,
  Form,
  Input,
  PageHeader,
  Row,
  Select,
  Switch,
  Upload,
  notification,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { useAuth } from "../../context/auth";
import IconAdvertise from "../../static/icon/IconAdvertise";

const POPUP_TYPES = [
  { value: "register", label: "Register" },
  { value: "home", label: "Home" },
];

const IMAGE_LIMIT = 2 * 1024 * 1024;
const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/webp"];

// Kept in step with the limits enforced in charger popup +server.ts.
const TITLE_LIMIT = 50;
const DETAIL_LIMIT = 100;

const PopupSetting = ({ match: { params: { id } } }) => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [change, setChange] = useState(false);
  const [disabled, setDisabled] = useState(false);

  // The picked File object (new upload), and a preview URL for the current image.
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (id === 'new') return;

    async function getPopup() {
      const url = new URL('/api/popup/' + id, window.location);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { popup } = await res.json();
        form.setFieldsValue(popup);
        if (popup.imgUrl) setPreviewUrl(`/api/upload/${popup.imgUrl}`);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setChange(false);
    getPopup();
  }, [accessToken, form, id]);

  const beforeUpload = (picked) => {
    if (!IMAGE_ACCEPT.includes(picked.type)) {
      notification["error"]({ message: "Only PNG, JPG or WEBP images are allowed." });
      return Upload.LIST_IGNORE;
    }
    if (picked.size > IMAGE_LIMIT) {
      notification["error"]({ message: "Image must be smaller than 2 MB." });
      return Upload.LIST_IGNORE;
    }

    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
    setChange(true);
    return false; // prevent antd auto-upload
  };

  const save = async (values) => {
    if (id === 'new' && !file) {
      notification["error"]({ message: "Image is required." });
      return;
    }

    setDisabled(true);
    setChange(false);

    const formData = new FormData();
    formData.append('type', values.type);
    formData.append('titleEn', values.titleEn);
    formData.append('titleTh', values.titleTh);
    formData.append('detailEn', values.detailEn);
    formData.append('detailTh', values.detailTh);
    formData.append('tel', values.tel ?? '');
    formData.append('active', String(!!values.active));
    if (file) formData.append('img', file);

    const url = new URL(id === 'new' ? '/api/popup' : '/api/popup/' + id, window.location);

    const res = await fetch(url, {
      method: id === 'new' ? 'POST' : 'PATCH',
      headers: { 'X-ACCESS-TOKEN': accessToken }, // no content-type — browser sets multipart boundary
      body: formData,
    });
    if (res.ok) {
      notification["success"]({
        message: "Successfully saved data.",
      });
      history.push({
        pathname: "/popup",
        searchValue: history.location.searchValue,
        pageValue: history.location.pageValue,
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
      setChange(true);
    }

    setDisabled(false);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconAdvertise /> &nbsp;&nbsp;Popup Management
            </span>
          }
        />
      </div>
      <div className="content">
        <div className="search">
          <Form
            form={form}
            onFinish={save}
            onFieldsChange={() => setChange(true)}
            className="search_inline row mt-3"
            style={{ display: "inline-grid", margin: "auto", width: "80%" }}
          >
            <Row gutter={[48, 16]}>
              <Col span={24}>
                <Form.Item
                  label="Type"
                  name="type"
                  colon={false}
                  className="mt-2"
                  rules={[{ required: true, message: "Type is required." }]}
                >
                  <Select
                    placeholder="Select type"
                    options={POPUP_TYPES}
                    disabled={id !== 'new'}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Image (recommended 400 × 400 px, max 2 MB)"
                  colon={false}
                  className="mt-2"
                >
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    showUploadList={false}
                    accept={IMAGE_ACCEPT.join(",")}
                    beforeUpload={beforeUpload}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="popup"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Title (EN)"
                  name="titleEn"
                  colon={false}
                  className="mt-2"
                  rules={[{ required: true, message: "Title (EN) is required." }]}
                >
                  <Input showCount maxLength={TITLE_LIMIT} placeholder="Title (EN)" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Detail (EN)"
                  name="detailEn"
                  colon={false}
                  className="mt-2"
                  rules={[{ required: true, message: "Detail (EN) is required." }]}
                >
                  <Input.TextArea showCount rows={3} maxLength={DETAIL_LIMIT} placeholder="Detail (EN)" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Title (TH)"
                  name="titleTh"
                  colon={false}
                  className="mt-2"
                  rules={[{ required: true, message: "Title (TH) is required." }]}
                >
                  <Input showCount maxLength={TITLE_LIMIT} placeholder="Title (TH)" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Detail (TH)"
                  name="detailTh"
                  colon={false}
                  className="mt-2"
                  rules={[{ required: true, message: "Detail (TH) is required." }]}
                >
                  <Input.TextArea showCount rows={3} maxLength={DETAIL_LIMIT} placeholder="Detail (TH)" />
                </Form.Item>
              </Col>

              <Col span={24}>
                {/* Required for Register only, so it has to react to the Type field. */}
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.type !== cur.type}
                >
                  {({ getFieldValue }) => {
                    const required = getFieldValue('type') === 'register';

                    return (
                      <Form.Item
                        label={`Contact Number${required ? '' : ' (optional)'}`}
                        name="tel"
                        colon={false}
                        className="mt-2"
                        rules={required ? [{ required: true, message: "Contact Number is required for Register popup." }] : []}
                      >
                        <Input maxLength={20} placeholder="Contact Number" />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={24} className="flex space-x-4 justify-end ">
                <Form.Item
                  label=""
                  name="active"
                  colon={false}
                  className="mt-2"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch />
                </Form.Item>
                <span className="font-bold mt-2.5  ">Inactive/Active</span>
              </Col>
            </Row>

            <div className="w-full text-center flex justify-center space-x-4">
              <Button
                className="w-36"
                type="primary"
                ghost
                shape="round"
                htmlType="button"
                onClick={() => {
                  history.push({
                    pathname: "/popup",
                    searchValue: history.location.searchValue,
                    pageValue: history.location.pageValue,
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                className="w-36"
                key="save"
                type="primary"
                shape="round"
                htmlType="submit"
                disabled={disabled}
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <Prompt when={change} message="Changes will not be saved. Do you want to proceed ?" />
    </>
  );
};

export default PopupSetting;
